import { ReactNode, useEffect, useMemo, useState } from "react"

// material-ui
import { alpha, useTheme } from "@mui/material/styles"
import { AppBar, Toolbar, useMediaQuery, AppBarProps } from "@mui/material"

// project-imports
import AppBarStyled from "./AppBarStyled"
import HeaderContent from "./HeaderContent"
import IconButton from "components/@extended/IconButton"

import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from "config"
import useConfig from "hooks/useConfig"
import useThemeMode from "hooks/useThemeMode"
import { dispatch, useSelector } from "store"
import { openDrawer } from "store/reducers/menu"

// assets
import { HambergerMenu, TaskSquare, DirectInbox, Moon, Sun1 } from "iconsax-react"

// types
import { MenuOrientation, ThemeMode } from "types/config"
import { useNavigate } from "react-router"
import { KSpan } from "kahuna-base-react-components"
import axios from "utils/axios"

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

const Header = () => {
  const [inboxCount, setInboxCount] = useState(0)
  const theme = useTheme()
  const downLG = useMediaQuery(theme.breakpoints.down("lg"))

  const { menuOrientation } = useConfig()
  const { isDark, toggleThemeMode } = useThemeMode()
  const { drawerOpen } = useSelector((state) => state.menu)

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG

  // header content
  const headerContent = useMemo(() => <HeaderContent />, [])

  const fetchInboxCount = () => {
    axios.get(`/workflow/inbox/count/`).then((resp) => {
      if (resp.data.count) {
        setInboxCount(resp.data.count)
      }
    })
  }

  useEffect(() => {
    fetchInboxCount()
  }, [])

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? "secondary.200" : "secondary.200"
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? "background.default" : "secondary.100"

  const navigate = useNavigate()

  // common header
  const mainHeader: ReactNode = (
    <Toolbar sx={{ px: { xs: 2, sm: 4.5, lg: 8 } }}>
      {!isHorizontal ? (
        <div className="flex gap-4">
          <IconButton
            aria-label="open drawer"
            onClick={() => dispatch(openDrawer(!drawerOpen))}
            edge="start"
            color="secondary"
            variant="light"
            size="large"
            sx={{
              color: "secondary.main",
              bgcolor: drawerOpen ? iconBackColorOpen : iconBackColor,
              ml: { xs: 0, lg: -2 },
              p: 1
            }}
          >
            <HambergerMenu />
          </IconButton>
          <IconButton
            aria-label="tasks"
            onClick={() => navigate(`/tasks/`)}
            edge="start"
            color="secondary"
            variant="light"
            size="large"
            sx={{
              color: "secondary.main",
              bgcolor: drawerOpen ? iconBackColorOpen : iconBackColor,
              ml: { xs: 0, lg: -2 },
              p: 1
            }}
          >
            <TaskSquare />
          </IconButton>
          <div>
            <IconButton
              aria-label="inbox"
              onClick={() => navigate(`/inbox/`)}
              edge="start"
              color="secondary"
              variant="light"
              size="large"
              sx={{
                color: "secondary.main",
                bgcolor: drawerOpen ? iconBackColorOpen : iconBackColor,
                ml: { xs: 0, lg: -2 },
                p: 1
              }}
            >
              <DirectInbox />
            </IconButton>
            {inboxCount && (
              <div style={{ position: "absolute", marginLeft: 18, marginTop: -44 }}>
                <div style={{ background: "red", borderRadius: 15 }} className="pl-2 pr-2">
                  <KSpan text={inboxCount?.toString() || "0"} color="white" />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
      {headerContent}
      <IconButton
        data-testid="header-theme-mode-toggle"
        aria-label={isDark ? "switch to light mode" : "switch to dark mode"}
        onClick={toggleThemeMode}
        color="secondary"
        variant="light"
        size="large"
        sx={{ color: "secondary.main", bgcolor: iconBackColor, ml: "auto", p: 1 }}
      >
        {isDark ? <Sun1 /> : <Moon />}
      </IconButton>
    </Toolbar>
  )

  // app-bar params
  const appBar: AppBarProps = {
    position: "fixed",
    elevation: 0,
    sx: {
      bgcolor: alpha(theme.palette.background.default, 0.8),
      backdropFilter: "blur(8px)",
      zIndex: 1200,
      width: isHorizontal
        ? "100%"
        : { xs: "100%", lg: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : `calc(100% - ${MINI_DRAWER_WIDTH}px)` }
    }
  }

  return (
    <>
      {!downLG ? (
        <AppBarStyled open={drawerOpen} {...appBar}>
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AppBar {...appBar}>{mainHeader}</AppBar>
      )}
    </>
  )
}

export default Header
